import { BEIGE_500, BEIGE_600, BOX_SHADOW_MD, GRAY_200, GRAY_700, GRAY_900, WHITE } from 'constants/styling/theme'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import moment, { Moment } from 'moment'
import { useCallback, useEffect, useRef, useState } from 'react'

import { BKBNThemeWrapper } from 'components/common/BKBNThemeWrapper'
import Box from '@mui/material/Box'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { MUIButton } from 'components/common/MUIButton'
import { MUIDivider } from 'components/common/MUIDivider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import styles from './DateRangePicker.module.sass'
import { useTranslation } from 'react-i18next'

enum QuickSelectionType {
  WEEK = 'week',
  MONTH = 'month',
  LAST_6_MONTHS = 'last6months'
}

export interface DateRangePickerProps {
  startDate: Moment | null
  endDate: Moment | null
  onDateRangeChange: (startDate: Moment | null, endDate: Moment | null) => void
  onTempDateRangeChange?: (startDate: Moment | null, endDate: Moment | null) => void
  onClear?: () => void
  onClose?: () => void
  timezone?: string
  minDate?: Moment
  maxDate?: Moment
  allowPastOnly?: boolean
}

const CustomDay = (props: PickersDayProps<Moment> & { startDate?: Moment | null; endDate?: Moment | null; onDateClick?: (date: Moment) => void; allowPastOnly?: boolean }) => {
  const { startDate, endDate, day, outsideCurrentMonth, onDateClick, allowPastOnly, ...other } = props

  const isStartDate = startDate && day.isSame(startDate, 'day')
  const isEndDate = endDate && day.isSame(endDate, 'day')
  const isInRange = startDate && endDate && day.isBetween(startDate, endDate, 'day', '[]')
  const isFutureDate = allowPastOnly && day.isAfter(moment(), 'day')
  const isDisabled = isFutureDate

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.nativeEvent.stopImmediatePropagation()
    if (!isDisabled) {
      onDateClick?.(day)
    }
  }

  return (
    <PickersDay
      {...other}
      day={day}
      outsideCurrentMonth={outsideCurrentMonth}
      onClick={handleClick}
      data-selected={isStartDate || isEndDate ? 'true' : 'false'}
      sx={{
        borderRadius: '50%',
        margin: 0,
        ...(isDisabled && {
          color: GRAY_200,
          cursor: 'not-allowed',
          opacity: 0.5,
          '&:hover': {
            backgroundColor: 'transparent',
            cursor: 'not-allowed',
          },
        }),
        ...(isStartDate && {
          backgroundColor: GRAY_900,
          color: 'white',
          borderRadius: '50% !important',
          '&:hover': {
            backgroundColor: GRAY_900,
          },
        }),
        ...(isEndDate && {
          backgroundColor: GRAY_900,
          color: 'white',
          borderRadius: '50% !important',
          '&:hover': {
            backgroundColor: GRAY_900,
          },
        }),
        ...(isInRange && !isStartDate && !isEndDate && {
          backgroundColor: BEIGE_500,
          borderRadius: '0 !important',
          margin: '0 !important',
          '&:hover': {
            backgroundColor: BEIGE_500,
          },
        }),
      }}
    />
  )
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateRangeChange,
  onTempDateRangeChange,
  onClear,
  onClose,
  timezone,
  minDate,
  maxDate,
  allowPastOnly = false,
}) => {
  const { t } = useTranslation(['properties_dashboard'])
  const [currentMonth, setCurrentMonth] = useState<Moment | null>(moment())
  const [tempStartDate, setTempStartDate] = useState<Moment | null>(startDate)
  const [tempEndDate, setTempEndDate] = useState<Moment | null>(endDate)
  const calendarRef = useRef<HTMLDivElement>(null)

  // Sync internal temp state with parent props when they change
  useEffect(() => {
    setTempStartDate(startDate)
    setTempEndDate(endDate)
  }, [startDate, endDate])

  const quickSelectionOptions = [
    { key: QuickSelectionType.WEEK, translationKey: 'this_week' },
    { key: QuickSelectionType.MONTH, translationKey: 'this_month' },
    { key: QuickSelectionType.LAST_6_MONTHS, translationKey: 'last_6_months' },
  ] as const

  const handleDateClick = useCallback((date: Moment) => {
    // If allowPastOnly is true, prevent selection of future dates
    if (allowPastOnly && date.isAfter(moment(), 'day')) {
      return
    }

    let newStartDate = tempStartDate
    let newEndDate = tempEndDate

    if (!tempStartDate || (tempStartDate && tempEndDate)) {
      // Start new selection
      newStartDate = date
      newEndDate = null
    } else {
      // Complete the selection
      if (date.isBefore(tempStartDate)) {
        newStartDate = date
        newEndDate = tempStartDate
      } else {
        newEndDate = date
      }
    }

    setTempStartDate(newStartDate)
    setTempEndDate(newEndDate)

    // Update temp display in input field
    onTempDateRangeChange?.(newStartDate, newEndDate)
  }, [tempStartDate, tempEndDate, onTempDateRangeChange, allowPastOnly])

  const handleMonthChange = useCallback((date: Moment) => {
    setCurrentMonth(date)
  }, [])

  const handleApply = () => {
    // Save the selected date range and close the dropdown
    onDateRangeChange(tempStartDate, tempEndDate)
    onClose?.()
  }

  const handleClear = () => {
    setTempStartDate(null)
    setTempEndDate(null)
    onDateRangeChange(null, null)
    onClear?.()
  }

  const handleQuickSelect = (type: QuickSelectionType) => {
    let newStartDate: Moment
    let newEndDate: Moment

    switch (type) {
      case QuickSelectionType.WEEK:
        newStartDate = moment().startOf('week')
        newEndDate = moment().endOf('week')
        break
      case QuickSelectionType.MONTH:
        newStartDate = moment().startOf('month')
        newEndDate = moment().endOf('month')
        break
      case QuickSelectionType.LAST_6_MONTHS:
        newStartDate = moment().subtract(6, 'months').startOf('month')
        newEndDate = moment().endOf('month')
        break
      default:
        newStartDate = moment()
        newEndDate = moment()
        break
    }

    // If allowPastOnly is true, ensure we don't select future dates
    if (allowPastOnly) {
      const today = moment()
      if (newEndDate.isAfter(today, 'day')) {
        newEndDate = today
      }
      if (newStartDate.isAfter(today, 'day')) {
        newStartDate = today
      }
    }

    setCurrentMonth(null)
    setTempStartDate(newStartDate)
    setTempEndDate(newEndDate)

    // Update temp display in input field
    onTempDateRangeChange?.(newStartDate, newEndDate)
    // Don't call onDateRangeChange here - only update temp state
    // User must click Apply to actually apply the selection!!!
  }

  return (
    <BKBNThemeWrapper>
      <Box sx={{
        width: '100%',
        backgroundColor: WHITE,
        boxShadow: BOX_SHADOW_MD,
        borderRadius: '0.8rem',
      }}
        onClick={(e) => e.stopPropagation()}
        data-dropdown-container
      >
        <Stack gap="1.2rem" sx={{ padding: '1.6rem' }}>
          <Typography variant="text-sm" fontWeight={400} color={GRAY_700} sx={{ fontSize: '1.4rem' }}>
            {allowPastOnly ? t('select_past_time_frame') : t('select_time_frame')}
          </Typography>

          {/* Quick selection buttons - this week, this month, last 6 months... */}
          <Stack flexDirection="row" gap="0.8rem" paddingRight={0.4} flexWrap="wrap">
            {quickSelectionOptions.map(({ key, translationKey }) => (
              <MUIButton
                key={key}
                size="sm"
                type="secondaryBorder"
                borderStyle='rounded-sm'
                onClick={() => handleQuickSelect(key as QuickSelectionType)}
                className={styles.quickSelectionButton}
              >
                {t(translationKey)}
              </MUIButton>
            ))}
          </Stack>

          <Typography variant="text-xs" color={GRAY_700} fontWeight={400}>
            {t('or_select_time_frame')}
          </Typography>

          {/* Calendar */}
          <Box
            ref={calendarRef}
            sx={{
              border: `1px solid ${BEIGE_600}`,
              borderRadius: '0.8rem',
            }}
            data-dropdown-container
          >
            <DateCalendar
              value={currentMonth}
              onChange={handleMonthChange}
              onMonthChange={handleMonthChange}
              minDate={minDate}
              maxDate={allowPastOnly ? moment() : maxDate}
              timezone={timezone}
              dayOfWeekFormatter={(day) => day.slice(0, 3)}
              shouldDisableDate={(date) => allowPastOnly ? date.isAfter(moment(), 'day') : false}
              slots={{
                day: (dayProps) => (
                  <CustomDay
                    {...dayProps}
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    onDateClick={handleDateClick}
                    allowPastOnly={allowPastOnly}
                  />
                ),
              }}

              className={styles.calendarContainer}
            />
          </Box>
        </Stack>

        <MUIDivider margin={8} />

        {/* Action buttons */}
        <Stack flexDirection="row" gap="0.8rem" justifyContent="flex-end" sx={{ padding: '0 1.6rem 0.8rem 0' }}>
          <MUIButton
            size="sm"
            type="secondaryBorder"
            onClick={handleClear}
            sx={{
              fontSize: '1.4rem',
              fontWeight: 500,
              padding: '0.8rem 2.4rem',
              backgroundColor: 'transparent',
              color: GRAY_900,
              border: 'none',
              width: 'fit-content',
              '&:hover': {
                backgroundColor: GRAY_200,
              }
            }}
          >
            {t('clear_selection')}
          </MUIButton>
          <MUIButton
            size="sm"
            type="defaultPrimary"
            onClick={handleApply}
            sx={{
              fontSize: '1.4rem',
              padding: '0.8rem 1.6rem',
              width: 'fit-content',
            }}
          >
            {t('apply')}
          </MUIButton>
        </Stack>
      </Box>
    </BKBNThemeWrapper>
  )
}
